import { useContext, useState, useEffect, useMemo } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { AppContext } from '../../containers/App/context';
import { useMediaQuery } from '@fortress-technology-solutions/fortress-component-library/hooks';
import { useMenuToggle } from '@fortress-technology-solutions/fortress-component-library/Molecules';
import {
  useTwoWayNotifications,
  usePortfolioTwoWayStatus,
} from '../../containers/App/Routes/MainLayoutRoutes/hooks';
import useNavigationOptions from './hooks.useNavigationOptions';
import { getCookie } from '../../utils/cookie';

const useAppBarNavDrawer = ({
  actions,
  navigationOptions,
  paymentProvider,
  locale,
  selectedPropertySubjournals,
  history,
}) => {
  const {
    mincTransactionScreen,
    affordableWaitlist,
    globalSearch,
    navigationMenuUxImprovements,
    leftsideNavButton,
    ...restFlags
  } = useFlags();

  const flags = useMemo(
    () => ({
      ...restFlags,
      mincTransactionScreen: mincTransactionScreen,
      affordableWaitlistScreen: affordableWaitlist,
      globalSearch,
      navigationMenuUxImprovements,
      leftsideNavButton,
    }),
    [
      restFlags,
      mincTransactionScreen,
      affordableWaitlist,
      globalSearch,
      navigationMenuUxImprovements,
      leftsideNavButton,
    ],
  );

  const {
    permissions,
    selectedProperty,
    onSelectProperty,
    onSelectPortfolioSummary,
    logOut,
  } = useContext(AppContext);

  const [fullName, setFullName] = useState('');
  const sessionId = getCookie('session_id');
  useEffect(() => {
    if (sessionId?.length && sessionId !== 'undefined') {
      let decoded = JSON.parse(atob(sessionId.split('.')[1]));
      setFullName(`${decoded.firstName} ${decoded.lastName}`);
      actions.socketConnect();
    }
  }, [actions, sessionId]);

  const twoWayNotifications = useTwoWayNotifications({ selectedProperty });
  const hasActiveTwoWayProperties = usePortfolioTwoWayStatus();
  const isXs = useMediaQuery('only', 'xs');
  const isSmallerThanMd = useMediaQuery('down', 'md');
  const [drawerHovered, setDrawerHovered] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (flags.leftsideNavButton) {
      if (isSmallerThanMd) {
        setOpen(false);
      } else {
        setOpen(true);
      }
    }
  }, [flags.leftsideNavButton, isSmallerThanMd]);

  const {
    open: userMenuOpen,
    handleToggle: handleUserMenuToggle,
    handleClose: handleUserMenuClose,
    anchorRef: userMenuAnchorRef,
  } = useMenuToggle(false);

  const navOptionsArray = useNavigationOptions({
    navigationOptions,
    selectedProperty,
    permissions,
    paymentProvider,
    flags,
    isXs,
    locale,
    selectedPropertySubjournals,
    history,
    setOpen,
    twoWayNotifications,
    hasActiveTwoWayProperties,
  });

  const useMobileDrawer = flags.leftsideNavButton ? isSmallerThanMd : isXs;
  const showNavText = !useMobileDrawer ? drawerHovered || open : true;

  return {
    drawerHovered,
    flags,
    fullName,
    handleUserMenuClose,
    handleUserMenuToggle,
    isXs,
    isSmallerThanMd,
    logOut,
    navOptionsArray,
    onSelectPortfolioSummary,
    onSelectProperty,
    open,
    selectedProperty,
    setDrawerHovered,
    setOpen,
    showNavText,
    useMobileDrawer,
    userMenuAnchorRef,
    userMenuOpen,
  };
};

export default useAppBarNavDrawer;
